/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, CircularProgress, Stack, TextField, Typography, Grid, Button as MuiButton } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import Header from "components/Header";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import { Controller, useForm } from "react-hook-form";
import './bank.css'
import { ApolloError, useQuery } from "@apollo/client";
import { athletesBankAccountDetailsResponse } from "types/bankAccount";
import { bankAccountDetails } from "queries/bankAccount";
import { insertApiResponseDataDog, insertApiResponseDataDogError } from "utils";
import { useNotifications } from "providers/Notification";
import AddAccountButton from "../AddAccountButton";
import useUser from "hooks/useUser";
import { Navigate, useNavigate } from "react-router-dom";
import { FileUpload } from "../../../components/FileUpload/fileupload";
import { Put } from "providers/Fetch";
import AccountInfo from "../accountInfo/info";
import { StripeVerificationStatus } from "types";
import { getPaymentRequirementsStatus } from "../helper";
import background from "assets/images/SignUp-Successv2.png";
import PaymentVerificationErrorDialog from "../PersonalDetails/PaymentVerificationErrorDialog"; // Import the dialog component

interface PersonalDetailsFormValues {
    firstName: string;
    bankAccountType: string;
    routingNumber: string;
    accountNumber: string;
    bankStatementDocumentFileFront: File | null;
}

const BankDetails = () => {
    const notify = useNotifications();
    const navigate = useNavigate()
    const user = useUser();
    const { hasBankFieldsDue } = getPaymentRequirementsStatus(user);
    if (user === null) return <CircularProgress />;
    // State for controlling the verification error dialog
    const [showVerificationDialog, setShowVerificationDialog] = useState(false);
    // Flag to track if user has already acknowledged the dialog
    const [hasAcknowledgedDialog, setHasAcknowledgedDialog] = useState(false);
    // Refs for form fields to focus after clearing
    const bankAccountTypeRef = useRef<HTMLInputElement>(null);
    const routingNumberRef = useRef<HTMLInputElement>(null);
    const accountNumberRef = useRef<HTMLInputElement>(null);

    // Check if user is an athlete and doesn't have a payment connected account
    if (!user.paymentConnectedAccountId) {
        return <Navigate to="/home/payment" replace />;
    }
    // Parse payment requirements once
    const userRequirements = JSON.parse(user?.paymentRequirements || '{}');
    const isPendingVerification = user?.paymentVerificationStatus === StripeVerificationStatus.PENDING;
    const isNotVerified = user?.paymentVerificationStatus !== StripeVerificationStatus.VERIFIED;
    const hasRequirements = user?.paymentRequirements !== null;

    // Show AccountInfo if migrant OR verification is pending with no currently_due fields
    if (
        user.isMigrant ||
        (isNotVerified && isPendingVerification && hasBankFieldsDue !== null && userRequirements.currently_due?.length === 0)
    ) {
        return <AccountInfo />;
    }

    // Show AccountInfo if migrant OR verification is pending but no paymentRequirements data yet
    if (
        user.isMigrant ||
        (user?.paymentConnectedAccountId && isPendingVerification && !hasRequirements)
    ) {
        return <AccountInfo />;
    }

    const { loading, data, refetch } = useQuery<athletesBankAccountDetailsResponse>(bankAccountDetails, {
        onError: (error: ApolloError) => {
            const err: any = error
            insertApiResponseDataDog("bankAccountDetailsWithCko(error)", err, "error")
            insertApiResponseDataDogError(error, "bankAccountDetailsWithCko(error)");
            notify.error(error.message);
        }, onCompleted: (data) => {
            insertApiResponseDataDog("bankAccountDetailsWithCko(success)", data, "info")
        }
    });
    const getFirstName = () => {
        if (user?.ckoFirstName) return user.ckoFirstName;
        if (user?.firstName) return user.firstName;
        return "";
    };

    const form = useForm<PersonalDetailsFormValues>({
        defaultValues: {
            firstName: getFirstName(), // Set firstName from user object
            bankAccountType: "",
            accountNumber: "",
            routingNumber: "",
            bankStatementDocumentFileFront: null
        },
        mode: "all",
    });
    const { isDirty, errors } = form.formState;
    const [accountTypeBg, setBankAccounttypeBg] = useState(false);
    const [routerBg, setRouterBg] = useState(false);
    const [athleteStatus, setAthleteStatus] = useState<any>('')
    const [bankAccountBg, setBankAccountBg] = useState(false);
    const [bankFile, setBankFile] = useState<File | null>(form.getValues('bankStatementDocumentFileFront') || null);
    const [apiLoading, setApiLoading] = useState(false);
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);
    // Function to clear fields that are due
    const clearDueFields = () => {
        if (!user?.paymentRequirements) return;

        const paymentRequirements = JSON.parse(user.paymentRequirements);
        const currentlyDue = paymentRequirements?.currently_due || [];

        // Clear bank fields that are due
        if (currentlyDue.includes("external_account") ||
            currentlyDue.includes("documents.bank_account_ownership_verification.files")) {
            form.setValue("bankAccountType", "");
            form.setValue("routingNumber", "");
            form.setValue("accountNumber", "");
            bankAccountTypeRef.current?.focus();
        }

        // Clear bank document if required
        if (currentlyDue.includes("external_account_verification_document") ||
            currentlyDue.includes("documents.bank_account_ownership_verification.files")) {
            form.setValue("bankStatementDocumentFileFront", null);
            setBankFile(null);
        }

        // Trigger form validation to show errors for empty fields
        form.trigger();
    };

    // Check if verification dialog should be shown (only if not already acknowledged)
    useEffect(() => {
        if (!hasAcknowledgedDialog &&
            user?.paymentConnectedAccountId &&
            user?.paymentVerificationStatus === StripeVerificationStatus?.PENDING &&
            hasBankFieldsDue) {
            setShowVerificationDialog(true);
        } else {
            setShowVerificationDialog(false);
        }
    }, [user, hasBankFieldsDue, hasAcknowledgedDialog]);

    useEffect(() => {
        if (data) {
            if (data.getAthleteAccountDetailsCko?.status === 400) {
                setAthleteStatus(data.getAthleteAccountDetailsCko.status)
            } else if (data?.getAthleteAccountDetailsCko?.status === 200) {
                const res = JSON.parse(String(data.getAthleteAccountDetailsCko.data));
                const maskedAccountNumber = res?.last4
                    ? `${"*".repeat(8)}${res.last4}`
                    : "";

                if (maskedAccountNumber) {
                    setBankAccountBg(true);
                }
                form.setValue('accountNumber', maskedAccountNumber);
                form.setValue('bankAccountType', res?.bankAccountType || '');
                form.setValue('routingNumber', res?.routingNumber || '');
            } else if (data?.getAthleteAccountDetailsCko?.status === 201) {
                notify.success(data.getAthleteAccountDetailsCko.data);
            }

        }

    }, [data])

    useEffect(() => {
        refetch();
        user.refetch();
        // Update firstName in form when user data changes
        form.setValue('firstName', getFirstName());
    }, [user]);

    if (loading) return <CircularProgress />;

    const onSubmit = async (form: PersonalDetailsFormValues) => {
        // if (user?.ckoSubEntityBankStatus === 'pending') {
        //     notify.info('Your previous bank account submission is under review.');
        //     return;
        // }
        const formdata = new FormData();
        formdata.append("bankStatementDocumentFileFront", form.bankStatementDocumentFileFront as any);
        formdata.append("bankAccountType", form.bankAccountType);
        formdata.append("accountNumber", form.accountNumber.replace(/\*/g, "")); // remove * before submit
        formdata.append("routingNumber", form.routingNumber);
        setApiLoading(true);
        Put(`${process.env.REACT_APP_SERVER_URI}/stripe/athlete/bank-info`, formdata, true)
            .then(async (response) => response)
            .then((result: any) => {
                setApiLoading(false);
                insertApiResponseDataDog("updateAthleteBankAccountInfoApi(success)", result, "info");
                if (result.status > 300) {
                    try {
                        const parsed = JSON.parse(result.data);
                        let errorMessage = "Something went wrong. Please try again.";
                        if (parsed?.error?.message) {
                            errorMessage = parsed.error.message;
                        } else if (Array.isArray(parsed) && parsed.length > 0 && parsed[parsed.length - 1]?.description) {
                            errorMessage = parsed[parsed.length - 1].description;
                        } else if (typeof parsed === "string") {
                            errorMessage = parsed;
                        }
                        insertApiResponseDataDog("updateAthleteBankAccountInfoApi(error)", errorMessage, "error");
                        insertApiResponseDataDogError(errorMessage, "updateAthleteBankAccountInfoApi(error)");
                        notify.error(errorMessage);
                    } catch (e) {
                        const fallbackMessage = typeof result.data === "string" ? result.data : "An error occurred.";
                        insertApiResponseDataDog("updateAthleteBankAccountInfoApi(error)", fallbackMessage, "error");
                        insertApiResponseDataDogError(fallbackMessage, "updateAthleteBankAccountInfoApi(error)");
                        notify.error(fallbackMessage);
                    }
                } else {
                    // Success message
                    notify.success("Your bank information has been successfully processed and is currently under review by our payment processor.");
                    refetch();
                    setBankAccounttypeBg(false);
                    setRouterBg(false);
                    setBankFile(null);
                    // Check if user has paymentConnectedAccountId, paymentVerificationStatus is PENDING, and hasBankFieldsDue
                    if (user?.paymentConnectedAccountId &&
                        user?.paymentVerificationStatus === StripeVerificationStatus.PENDING &&
                        hasBankFieldsDue) {
                        setShowSuccessScreen(true);
                    }
                    const isPending = user?.paymentVerificationStatus === StripeVerificationStatus.PENDING;
                    const isVerified = user?.paymentVerificationStatus === StripeVerificationStatus.VERIFIED;

                    if (isPending) {
                        return <AccountInfo />;
                    }

                    if (isVerified) {
                        navigate("/home"); // Redirect to home page if verified
                        return null; // Optionally return null to prevent further rendering
                    }
                }
            })
            .catch((error) => {
                setApiLoading(false);
                insertApiResponseDataDogError(error, "updateAthleteBankAccountInfoApi(error)");
                insertApiResponseDataDog("updateAthleteBankAccountInfoApi(error)", error, "error");
                notify.error(error?.message || "An unexpected error occurred");
            });
    };

    // Show success screen if showSuccessScreen is true
    if (showSuccessScreen) {
        return (
            <Stack
                spacing={2}
                sx={{
                    position: "absolute",
                    top: "0px",
                    bottom: "0px",
                    left: "0px",
                    right: "0px",
                    backgroundImage: `url(${background})`,
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    zIndex: "0",
                    paddingTop: "100px",
                }}
            >
                <Grid style={{ width: 400, margin: "0 auto" }}>
                    <Typography
                        variant="h4"
                        component="h1"
                        align="left"
                        sx={{ color: "#fff", fontSize: "2rem", marginBottom: 5 }}
                    >{`Welcome, ${form.getValues("firstName")}!`}</Typography>

                    <Typography
                        sx={{
                            color: "#fff",
                            textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
                            marginBottom: 3,
                        }}
                        fontSize={16}
                        fontWeight={200}
                        align="left"
                    >
                        WHAT’S NEXT?{" "}
                    </Typography>

                    <Typography
                        sx={{
                            color: "#fff",
                            textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
                            marginBottom: 3,
                        }}
                        fontSize={16}
                        fontWeight={200}
                        align="left"
                    >
                        The processor needs to approve your account information and then you
                        will begin receiving payments.
                    </Typography>
                    <Typography
                        sx={{
                            color: "#fff",
                            textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
                        }}
                        fontSize={16}
                        fontWeight={200}
                        align="left"
                    >
                        This is a one-time process and shouldn’ take longer than 24-48
                        hours.{" "}
                    </Typography>
                    <Box style={{ margin: "0 auto" }}>
                        <MuiButton
                            variant="contained"
                            href="/home"
                            sx={{
                                background: "#FFF",
                                marginTop: 10,
                                color: "#592EE5",
                                width: "400px",
                                ":hover": { backgroundColor: "#f0ebff" },
                            }}
                        >
                            Back To Home{" "}
                        </MuiButton>
                    </Box>
                </Grid>
            </Stack>
        );
    }

    return (<>
        {athleteStatus && Number(athleteStatus) === 400 ?
            <Box marginTop={{ xs: "10px", md: "30px" }} padding="20px">
                <Stack textAlign="left">
                    <Typography fontSize="20px" fontWeight="600">
                        Please add this account
                    </Typography>
                    <AddAccountButton />
                </Stack>
            </Box> :
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Box className="ath-personal-details">
                    <Header text="Athlete payment info" />
                    <Typography fontSize="17px" fontWeight="600" color="red">
                        If you update your banking information, you will need to complete verification again by our bank processor
                    </Typography>
                    <Box sx={{ mt: 4 }} textAlign={"left"} className="personal-details__form">
                        <Stack sx={{ mt: 2 }} direction="column" alignItems="flex-start" className="bankinfo">
                            <Controller
                                name="bankAccountType"
                                control={form.control}
                                defaultValue=""
                                rules={{
                                    required: "Bank Account Type is required",
                                }}
                                render={({ field }) => (
                                    <TextField sx={{ mt: 3 }}
                                        select
                                        margin="dense"
                                        data-testid="NameOnAccount"
                                        label="Bank Account Type"
                                        placeholder="Bank Account Type"
                                        className={accountTypeBg ? "dropMenu" : ""}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        fullWidth
                                        autoComplete="none"
                                        inputRef={bankAccountTypeRef}
                                        inputProps={{
                                            "data-testid": "bankAccountType",
                                        }}
                                        error={Boolean(isDirty && errors.bankAccountType)}
                                        disabled={apiLoading}
                                        {...field}
                                    >
                                        <MenuItem value={'CHECKING'}>CHECKING</MenuItem>
                                        <MenuItem value={'SAVINGS'}>SAVINGS</MenuItem>

                                    </TextField>
                                )}
                            />
                            <Controller
                                name="routingNumber"
                                control={form.control}
                                defaultValue=""
                                rules={{
                                    required: "Routing Number is required",
                                    minLength: { value: 9, message: "Please Enter 9 minimum number" }
                                }}
                                render={({ field: { onChange, value, name, ref } }) => (
                                    <TextField sx={{ mt: 3 }}
                                        margin="dense"
                                        data-testid="BankId"
                                        label="Routing Number"
                                        placeholder="Routing Number"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        fullWidth
                                        autoComplete="none"
                                        inputRef={routingNumberRef}
                                        inputProps={{
                                            "data-testid": "bankId",
                                        }}
                                        error={Boolean(isDirty && errors.routingNumber)}
                                        disabled={apiLoading}
                                        helperText={(isDirty && errors.routingNumber?.message) || " "}
                                        className={routerBg ? "textFiled_bg" : ""}
                                        name={name}
                                        value={value}
                                        ref={ref}
                                        onChange={(e) => {
                                            const regex = /^[0-9\b]+$/;
                                            const isNumber = regex.test(e.target.value);
                                            if ((e.target.value && !isNumber) || e.target.value.length > 9) {
                                                return;
                                            }
                                            onChange(e);
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="accountNumber"
                                control={form.control}
                                defaultValue=""
                                rules={{
                                    required: "Bank Account Number is required",
                                    validate: (value) => {
                                        // Allow masked values (with asterisks) for display
                                        if (value.includes('*')) {
                                            return true;
                                        }
                                        const rawValue = value.replace(/\*/g, ""); // remove masking
                                        if (!rawValue) {
                                            return "Bank Account Number is required";
                                        }
                                        if (rawValue.length < 6) {
                                            return "Please enter at least 6 digits";
                                        }
                                        if (rawValue.length > 34) {
                                            return "Please enter not more than 34 digits";
                                        }
                                        if (!/^\d+$/.test(rawValue)) {
                                            return "Bank Account Number must be numeric";
                                        }
                                        return true;
                                    }
                                }}
                                render={({ field: { onChange, value, name, ref } }) => (
                                    <TextField
                                        sx={{ mt: 3 }}
                                        margin="dense"
                                        data-testid="Bank Account Id"
                                        label="Bank Account Number"
                                        placeholder="Bank Account Number"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        className={bankAccountBg ? "textFiled_bg" : ""}
                                        fullWidth
                                        autoComplete="none"
                                        inputRef={accountNumberRef}
                                        inputProps={{
                                            "data-testid": "accountNumber",
                                        }}
                                        error={Boolean(isDirty && errors.accountNumber)}
                                        disabled={apiLoading}
                                        helperText={(isDirty && errors.accountNumber?.message) || " "}
                                        name={name}
                                        value={value}
                                        ref={ref}
                                        onChange={(e) => {
                                            const inputValue = e.target.value;
                                            const inputEvent = e.nativeEvent as InputEvent;
                                            const regex = /^[0-9\b]*$/;
                                            if (inputEvent.inputType === 'deleteContentBackward') {
                                                setBankAccountBg(true);
                                                onChange(e);
                                                return;
                                            }
                                            if (inputValue && !regex.test(inputValue)) {
                                                return;
                                            }
                                            setBankAccountBg(true);
                                            onChange(e);
                                        }}
                                    />
                                )}
                            />
                            <Box component={'span'} sx={{ mt: 3, width: "100%" }}>
                                <Typography
                                    style={{ color: '#592EE5', width: '100%', display: 'flex' }}
                                    component={'label'}
                                >
                                    Upload bank statement
                                </Typography>
                                <FileUpload
                                    disabled={apiLoading}
                                    acceptFileType=".jpg,.jpeg,.png,.pdf"
                                    setFile={(f) => {
                                        setBankFile(f);
                                        form.setValue('bankStatementDocumentFileFront', f);
                                    }}
                                    file={bankFile}
                                    maxFileSize={4000}
                                    formatErrorMessage="JPEG, JPG, PNG, or PDF"
                                />
                            </Box>
                        </Stack>
                    </Box>
                    <Button
                        sx={{ mt: 8 }}
                        startIcon={apiLoading ? <CircularProgress size={20} /> : null}
                        type="submit"
                        fullWidth
                        color="primary"
                        variant="contained"
                        disabled={Boolean(errors.accountNumber || errors.bankAccountType || errors.routingNumber || !bankFile || apiLoading)}
                    >
                        Save
                    </Button>
                </Box>
            </form>}

        {/* Payment Verification Error Dialog */}
        <PaymentVerificationErrorDialog
            open={showVerificationDialog}
            onClose={() => {
                setShowVerificationDialog(false);
                setHasAcknowledgedDialog(true);
            }}
            onProceed={() => {
                setShowVerificationDialog(false);
                setHasAcknowledgedDialog(true);
                clearDueFields();
            }}
            errorData={user?.paymentRequirements ? JSON.parse(user.paymentRequirements) : {}}
        />
    </>
    );

};
export default BankDetails;